import { pickT } from "@repo/shared";
import type { PdfCurrencyDisplay } from "@repo/shared/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { CurrencyDisplayField } from "./currency-display-field";
import { T } from "./portfolio-forecast-form.i18n";
import type { ComponentFormProps } from "./types";

export type PortfolioForecastConfig = {
  title?: string;
  description?: string;
  /** QL-414 — absent = the block's default. */
  currencyDisplay?: PdfCurrencyDisplay;
  showChart: boolean;
  showTable: boolean;
  compareWithBank?: boolean;
};

type PortfolioForecastFormProps = ComponentFormProps<PortfolioForecastConfig>;

export function PortfolioForecastForm({ config, onChange }: PortfolioForecastFormProps) {
  const t = useT(T);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="forecast-title">{t.title}</Label>
        <Input
          id="forecast-title"
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="forecast-description">{t.description}</Label>
        <Textarea
          id="forecast-description"
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      <CurrencyDisplayField
        blockType="portfolio_forecast"
        value={config.currencyDisplay}
        chartOnly={config.showChart !== false && !config.showTable}
        onChange={(currencyDisplay) => onChange({ ...config, currencyDisplay })}
      />

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="forecast-show-chart" className="text-base">
            {t.showChart}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showChartHelp}</p>
        </div>
        <Switch
          id="forecast-show-chart"
          checked={config.showChart !== false}
          onCheckedChange={(checked) => onChange({ ...config, showChart: checked })}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="forecast-show-table" className="text-base">
            {t.showTable}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showTableHelp}</p>
        </div>
        <Switch
          id="forecast-show-table"
          checked={config.showTable || false}
          onCheckedChange={(checked) => onChange({ ...config, showTable: checked })}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="forecast-compare-with-bank" className="text-base">
            {t.compareWithBank}
          </Label>
          <p className="text-sm text-muted-foreground">{t.compareWithBankHelp}</p>
        </div>
        <Switch
          id="forecast-compare-with-bank"
          checked={config.compareWithBank === true}
          onCheckedChange={(checked) => onChange({ ...config, compareWithBank: checked })}
        />
      </div>

      {!config.showChart && !config.showTable && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {t.validationError}
        </div>
      )}
    </div>
  );
}

export function validatePortfolioForecastConfig(
  config: PortfolioForecastConfig,
  lang = "en_GB",
): string | null {
  const t = pickT(T, lang);
  if (!config.showChart && !config.showTable) {
    return t.validationErrorLong;
  }
  return null;
}

export function getDefaultPortfolioForecastConfig(): PortfolioForecastConfig {
  return {
    title: "Portfolio Forecast",
    description: "Long-term growth projection for your portfolio.",
    showChart: true,
    showTable: false,
  };
}
